import { useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Popover, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarCheck,
  Check,
  CircleDot,
  Clock,
  ExternalLink,
  Filter,
  Mail,
  MailX,
  Phone,
  PhoneOff,
  ShieldCheck,
  ShieldAlert,
  X,
} from 'lucide-react'
import { VerificationOfEmployment } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import BulkActionBar from 'src/components/Core/BulkActionBar/BulkActionBar'
import DnTable from 'src/components/Core/DnTable'
import SortableHeader from 'src/components/DataDisplay/SortableHeader/SortableHeader'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import CompletionMethodModal from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import type { CompletionMethodValues } from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import { voeCompletionContext } from 'src/components/Overlays/CompletionMethodModal/voeCompletionContext'
import EmailSenderModal from 'src/components/Overlays/EmailSenderModal/EmailSenderModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useAutomatedOutreachEmail } from 'src/components/VoE/AutomatedOutreachPanel/useAutomatedOutreachEmail'
import ContactCountBadge, {
  type ContactBadgeInput,
} from 'src/components/VoE/ContactCountBadge/ContactCountBadge'
import NextActionTag from 'src/components/VoE/NextActionTag/NextActionTag'
import { useNoContactGuard } from 'src/components/VoE/NoContactGuard/useNoContactGuard'
import { useScheduledOutreachGuard } from 'src/components/VoE/ScheduledOutreachGuard/useScheduledOutreachGuard'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import {
  VoEResponseAutonomy,
  VoEResponseAutonomyLabels,
  VoEStatus,
} from 'src/graphql/types/verificationOfEmployments'
import {
  BULK_SEND_VOE_INSTRUCTIONS_EMAIL,
  BULK_UPDATE_VOE_STATUS,
  GET_VOE_LIST_ITEM,
  SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE,
} from 'src/graphql/voe.graphql'
import IconVoiceMail from 'src/icons/IconVoiceMail'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import { getEmployerClassificationLabel } from 'src/lib/employerClassification.utils'
import { buildFullName, formatSnakeValue } from 'src/lib/formatters'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import {
  formatInternationalPhoneNumber,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'
import { buildFmcsaCarrierUrl } from 'src/lib/url.utils'
import { knownPlatformLabel } from 'src/lib/voeProvider.utils'
import {
  DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  isExpiredEmployment,
  formatVerificationPeriod,
} from 'src/lib/voeVerificationPeriod'
import CustomTab from 'src/pages/ActivitiesPage/components/CustomTab'
import ContactModal from 'src/pages/ApplicantDetailsPage/tabs/VoETab/components/ContactModal/ContactModal'
import VoEDetails from 'src/pages/ApplicantDetailsPage/tabs/VoETab/components/VoEDetails/VoEDetails'
import VoEVoiceAgentButton from 'src/pages/ApplicantDetailsPage/tabs/VoETab/components/VoEVoiceAgentButton/VoEVoiceAgentButton'
import { buildVoEInstructionsEmailBody } from 'src/pages/ApplicantDetailsPage/tabs/VoETab/lib/voeEmailTemplate'
import { NEXT_ACTION_OWNER_OPTIONS } from 'src/pages/VoERequestsPage/nextActionFilter'

import { colorByStatus } from '../VoERequestsTable/VoERequestsTable'

dayjs.extend(relativeTime)

/**
 * The directory badge reads the same input shape as the employment table.
 * `carrierContactCount` is cast because the generated VerificationOfEmployment
 * type may predate the field.
 */
const toContactBadgeInput = (
  voe: VerificationOfEmployment
): ContactBadgeInput => {
  const suppression = voe.contactSuppression
  const suppressedContactTypes = [
    suppression?.email && 'EMAIL',
    suppression?.phoneNumber && 'PHONE',
    suppression?.faxNumber && 'FAX',
  ].filter((type): type is string => Boolean(type))
  return {
    dotNumber: voe.dotNumber,
    carrierContactCount: (
      voe as VerificationOfEmployment & { carrierContactCount?: number | null }
    ).carrierContactCount,
    contactName: voe.contactName,
    email: voe.email,
    phone: voe.phoneNumber,
    suppressedContactTypes,
  }
}

// Auto green, Assisted blue, Manual gray, None amber.
/**
 * Who did the work on the verification — automation, a recruiter, or both —
 * as a small tag beside the applicant's name, in the same spot and style as
 * the "Off" tag. A column of its own was tried and read as too heavy; this
 * only appears once the verification is answered or completed.
 */
const RESPONSE_TAG_CLASSES: Record<VoEResponseAutonomy, string> = {
  [VoEResponseAutonomy.AUTONOMOUS]:
    'bg-doubleNickel-success-50 text-doubleNickel-success-700',
  [VoEResponseAutonomy.ASSISTED]:
    'bg-doubleNickel-brand-25 text-doubleNickel-brand-700',
  [VoEResponseAutonomy.MANUAL]:
    'bg-doubleNickel-gray-100 text-doubleNickel-gray-600',
  [VoEResponseAutonomy.NO_OUTREACH]:
    'bg-doubleNickel-warning-50 text-doubleNickel-warning-600',
}

const ResponseTag = ({ voe }: { voe: VerificationOfEmployment }) => {
  const autonomy = voe.responseAutonomy as
    | VoEResponseAutonomy
    | null
    | undefined
  const meta = autonomy ? VoEResponseAutonomyLabels[autonomy] : undefined
  if (!autonomy || !meta) return null
  return (
    <Tooltip label={meta.tooltip} withArrow>
      <span
        data-testid="response-tag"
        className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${RESPONSE_TAG_CLASSES[autonomy]}`}
      >
        {meta.label}
      </span>
    </Tooltip>
  )
}

// ── Column Header Filter Dropdown ──────────────────────────────────────
interface FilterOption {
  label: string
  value: string
  icon?: React.ReactNode
}

interface ColumnFilterDropdownProps {
  options: FilterOption[]
  selected: string[]
  onChange: (selected: string[]) => void
}

const ColumnFilterDropdown = ({
  options,
  selected,
  onChange,
}: ColumnFilterDropdownProps) => {
  const [opened, setOpened] = useState(false)
  const hasActive = selected.length > 0

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <button
          className="flex-shrink-0 rounded p-0.5 hover:bg-doubleNickel-gray-200"
          onClick={(e) => {
            e.stopPropagation()
            setOpened((o) => !o)
          }}
        >
          <Filter
            className={`h-3 w-3 ${
              hasActive
                ? 'text-doubleNickel-brand-500'
                : 'text-doubleNickel-gray-400'
            }`}
          />
        </button>
      </Popover.Target>
      <Popover.Dropdown
        className="!rounded-lg !border-doubleNickel-gray-200 !p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex min-w-[160px] flex-col">
          {options.map((opt) => {
            const isSelected = selected.includes(opt.value)
            return (
              <button
                key={opt.value}
                className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors ${
                  isSelected
                    ? 'bg-doubleNickel-brand-50 text-doubleNickel-brand-600'
                    : 'text-doubleNickel-gray-700 hover:bg-doubleNickel-gray-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  toggle(opt.value)
                }}
              >
                {opt.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                <span className="flex-1">{opt.label}</span>
                {isSelected && (
                  <Check className="h-3 w-3 flex-shrink-0 text-doubleNickel-brand-500" />
                )}
              </button>
            )
          })}
          {hasActive && (
            <>
              <div className="my-1 border-t border-doubleNickel-gray-200" />
              <button
                className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-100"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange([])
                  setOpened(false)
                }}
              >
                <X className="h-3 w-3 flex-shrink-0" />
                Clear filters
              </button>
            </>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}

type TabCounts = Record<string, number | null> | null

export interface OutboundVoETableProps {
  loading: boolean
  voeList: VerificationOfEmployment[]
  totalCount: number
  onLoadPage: (pageInfo: { offset: number; limit: number }) => void
  sortField: string
  sortDirection: 'asc' | 'desc'
  onSortChange: (field: string, direction: 'asc' | 'desc') => void
  hiringStageTab: string
  onHiringStageTabChange: (tab: string) => void
  tabCounts: TabCounts
  toolbarControls?: React.ReactNode
  verificationPeriodMonths?: number
  // Last Contact column filters
  lastContactFilters: string[]
  onLastContactFiltersChange: (filters: string[]) => void
  // Contact column filters
  contactInfoFilters: string[]
  onContactInfoFiltersChange: (filters: string[]) => void
  // Employment Period column filter
  empPeriodFilters: string[]
  onEmpPeriodFiltersChange: (filters: string[]) => void
  // Consent column filter
  consentFilters: string[]
  onConsentFiltersChange: (filters: string[]) => void
  // Next action column filter
  nextActionFilters: string[]
  onNextActionFiltersChange: (filters: string[]) => void
  // Response column filter (admin/ops only)
  companyPhoneNumber?: string
}

const HIRING_STAGE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'hired', label: 'Hired' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'qualification', label: 'Qualification' },
  { value: 'application', label: 'Application' },
  { value: 'lead', label: 'Lead' },
]

const SORTABLE_FIELDS = {
  APPLICANT_NAME: 'Applicant',
  LAST_CONTACT_DATE: 'Last Attempt',
  NEXT_ACTION: 'Next action',
}

// Statuses available for bulk "Set Status" action — must match the filter menu
// in VoERequestsPage (ALL_VOE_STATUSES). Excludes REQUESTED which is not user-facing.
const BULK_STATUS_OPTIONS = [
  VoEStatus.NOT_STARTED,
  VoEStatus.IN_PROGRESS,
  VoEStatus.PENDING_REVIEW,
  VoEStatus.COMPLETED,
  VoEStatus.CANCELED,
  VoEStatus.DECLINED,
]

const TERMINAL_VOE_STATUSES = new Set([
  VoEStatus.COMPLETED,
  VoEStatus.CANCELED,
  VoEStatus.DECLINED,
])

// Types that count as actual contact attempts (matches backend CONTACT_ATTEMPT_TYPES).
// NOTE is excluded — it's an internal annotation, not an outreach attempt.
const CONTACT_ATTEMPT_TYPES = new Set(['CALL', 'EMAIL', 'FAX', 'ATS'])

// lastContactDate may not always be synced by the backend; fall back to
// the most recent contactDate in auditTrailLogs so the column is never
// blank when attempts exist.
const resolveLastContactDate = (
  voe: VerificationOfEmployment
): string | null => {
  if (voe.lastContactDate) return voe.lastContactDate as string
  const logs = voe.auditTrailLogs || []
  let latest: string | null = null
  for (const log of logs) {
    if (
      CONTACT_ATTEMPT_TYPES.has(log.type) &&
      log.contactDate &&
      (!latest || log.contactDate > latest)
    ) {
      latest = log.contactDate as string
    }
  }
  return latest
}

const OutboundVoETable = ({
  loading,
  voeList = [],
  totalCount,
  onLoadPage,
  sortField,
  sortDirection,
  onSortChange,
  hiringStageTab,
  onHiringStageTabChange,
  tabCounts,
  toolbarControls,
  verificationPeriodMonths = DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  lastContactFilters,
  onLastContactFiltersChange,
  contactInfoFilters,
  onContactInfoFiltersChange,
  empPeriodFilters,
  onEmpPeriodFiltersChange,
  consentFilters,
  onConsentFiltersChange,
  nextActionFilters,
  onNextActionFiltersChange,
  companyPhoneNumber,
}: OutboundVoETableProps) => {
  const { currentUser } = useAuth()
  const { handleOpen: openApplicantDrawer } = useApplicantDrawerContext()
  const phoneDeviceContext = usePhoneDevice()
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const [selectedVoe, setSelectedVoe] =
    useState<VerificationOfEmployment | null>(null)
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [emailVoe, setEmailVoe] = useState<VerificationOfEmployment | null>(
    null
  )
  const [emailModalOpened, { open: openEmailModal, close: closeEmailModal }] =
    useDisclosure(false)
  const [contactVoe, setContactVoe] = useState<VerificationOfEmployment | null>(
    null
  )
  const [
    contactModalOpened,
    { open: openContactModal, close: closeContactModal },
  ] = useDisclosure(false)
  const [
    bulkCompletionModalOpened,
    { open: openBulkCompletionModal, close: closeBulkCompletionModal },
  ] = useDisclosure(false)

  // Refetch VoE list when a VoE phone call disconnects
  const prevCallRef = useRef(phoneDeviceContext?.call)
  useEffect(() => {
    const wasOnCall = prevCallRef.current != null
    const callEnded = phoneDeviceContext?.call == null
    const isVoECall = phoneDeviceContext?.voeId != null
    if (wasOnCall && callEnded && isVoECall) {
      // Delay briefly to allow backend to process the call status callback
      setTimeout(() => {
        apolloClient.refetchQueries({
          include: ['VerificationOfEmployments', 'VoETabCounts'],
        })
      }, 2000)
    }
    prevCallRef.current = phoneDeviceContext?.call ?? null
  }, [phoneDeviceContext?.call])

  const toggleSelect = (voeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(voeId)) next.delete(voeId)
      else next.add(voeId)
      return next
    })
  }

  const allVisibleIds = voeList.map((v) => v.voeId)
  const singleSelectedVoe =
    selectedIds.size === 1
      ? voeList.find((v) => selectedIds.has(v.voeId)) ?? null
      : null
  const allSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id))

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(allVisibleIds))
    }
  }

  const apolloClient = useApolloClient()
  const [bulkUpdateStatus, { loading: bulkUpdatingStatus }] = useMutation(
    BULK_UPDATE_VOE_STATUS
  )
  const [bulkSendEmails, { loading: bulkSendingEmails }] = useMutation(
    BULK_SEND_VOE_INSTRUCTIONS_EMAIL
  )
  const [sendInstructionsEmail, { loading: sendingEmail }] = useMutation(
    SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE,
    {}
  )
  const refetchList = () =>
    apolloClient.refetchQueries({
      include: ['VerificationOfEmployments', 'VoETabCounts'],
    })

  const isBulkLoading = bulkUpdatingStatus || bulkSendingEmails

  /** Resolves true when the request went through (even with per-row failures). */
  const bulkSetStatus = async (
    status: VoEStatus,
    completion?: CompletionMethodValues
  ): Promise<boolean> => {
    try {
      const { data } = await bulkUpdateStatus({
        variables: {
          ids: [...selectedIds],
          status,
          ...(completion && {
            completion: {
              completionMethod: completion.completionMethod,
              platform: completion.platform,
              note: completion.note ?? null,
            },
          }),
        },
      })
      await refetchList()
      const { succeededCount, failedCount, skippedCount } =
        data.bulkUpdateVoEStatus
      const statusLabel = formatSnakeValue(status)
      const parts: string[] = []
      if (succeededCount > 0)
        parts.push(`${succeededCount} updated to ${statusLabel}`)
      if (skippedCount > 0) parts.push(`${skippedCount} already completed`)
      if (failedCount > 0) parts.push(`${failedCount} failed`)
      toast(parts.join(', '), failedCount > 0 ? 'error' : 'success')
      setSelectedIds(new Set())
      return true
    } catch {
      toast('Failed to update statuses', 'error')
      return false
    }
  }

  // Completing needs a method (and, for ATS, a platform) for every VoE, so
  // Completed asks first; the other statuses apply straight away.
  const onBulkStatusSelect = (status: VoEStatus) => {
    if (status === VoEStatus.COMPLETED) {
      openBulkCompletionModal()
      return
    }
    bulkSetStatus(status)
  }

  // Covers the list refetch after the mutation too, so Complete can't be
  // clicked again while the same selection is still showing.
  const [bulkCompleting, setBulkCompleting] = useState(false)
  const onBulkCompletionSubmit = async (values: CompletionMethodValues) => {
    setBulkCompleting(true)
    try {
      const succeeded = await bulkSetStatus(VoEStatus.COMPLETED, values)
      if (succeeded) closeBulkCompletionModal()
    } finally {
      setBulkCompleting(false)
    }
  }

  const bulkSendEmail = async () => {
    try {
      const { data } = await bulkSendEmails({
        variables: { voeIds: [...selectedIds] },
      })
      await refetchList()
      const { succeededCount, failedCount, skippedCount } =
        data.bulkSendVoEInstructionsEmail
      const parts: string[] = []
      if (succeededCount > 0) parts.push(`${succeededCount} sent`)
      if (failedCount > 0) parts.push(`${failedCount} failed`)
      if (skippedCount > 0) parts.push(`${skippedCount} skipped — no email`)
      const toastType =
        failedCount > 0 ? 'error' : succeededCount === 0 ? 'warning' : 'success'
      toast(parts.join(', '), toastType)
      setSelectedIds(new Set())
    } catch {
      toast('Failed to send emails', 'error')
    }
  }

  // Automation schedules its steps in a background run a moment after the
  // start, so the Next action column is read again once that has landed.
  const outreachEmail = useAutomatedOutreachEmail({
    component: 'OutboundVoETable',
    onStarted: () => {
      refetchList()
      setTimeout(refetchList, 2500)
    },
  })

  const { guardContact, noContactPrompt } = useNoContactGuard()
  const { guardScheduledOutreach, scheduledOutreachPrompt } =
    useScheduledOutreachGuard()

  const handleEmailClick = (voe: VerificationOfEmployment) =>
    guardContact(voe, () =>
      outreachEmail.requestEmail(
        {
          voeId: voe.voeId,
          email: voe.email,
          companyName: voe.companyName,
          applicantName: voe.applicant
            ? buildFullName(voe.applicant)
            : voe.driverName ?? '',
        },
        {
          sendManually: ({ email }) => {
            setEmailVoe(email ? { ...voe, email } : voe)
            openEmailModal()
          },
          notAutomated: () => {
            setEmailVoe(voe)
            openEmailModal()
          },
        }
      )
    )

  const handleCallClick = (voe: VerificationOfEmployment) =>
    guardContact(voe, () => {
      if (phoneDeviceContext?.phoneDevice?.isBusy) return
      dispatchPhoneAction({
        type: PhoneDeviceActionType.CALL,
        payload: {
          phone: unformatPhoneNumber(voe.phoneNumber),
          ...(voe.applicant?.applicantId && {
            applicantId: voe.applicant.applicantId,
          }),
          applicantFullName: voe.applicant
            ? buildFullName(voe.applicant)
            : voe.driverName || voe.companyName,
          voeId: voe.voeId,
          companyName: voe.companyName,
          voeEmail: voe.email,
        },
      })
    })

  const getRecipientEmail = (voe: VerificationOfEmployment): string => {
    return voe.email || ''
  }

  useEffect(() => {
    setSelectedIds(new Set())
  }, [voeList])

  // The selection clears whenever the list refetches; don't leave the bulk
  // completion modal open over a selection that is gone.
  useEffect(() => {
    if (selectedIds.size === 0) closeBulkCompletionModal()
  }, [selectedIds])

  // Keep the drawer's VoE in sync after mutations refetch the list. A VoE that
  // left the filtered list (completing it hides it under the default statuses)
  // is fetched on its own, or the drawer would keep showing it as it was.
  useEffect(() => {
    if (!selectedVoe) return
    const updated = voeList.find((v) => v.voeId === selectedVoe.voeId)
    if (updated) {
      setSelectedVoe(updated)
      return
    }
    const { voeId } = selectedVoe
    apolloClient
      .query({
        query: GET_VOE_LIST_ITEM,
        variables: { id: voeId },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const fresh = data?.verificationOfEmployment
        if (fresh) {
          setSelectedVoe((current) =>
            current?.voeId === voeId ? fresh : current
          )
        }
      })
      .catch(() => {})
  }, [voeList])

  // Refetch VoE list when a VoE call ends so audit trail logs update
  const prevCallState = useRef(phoneDeviceContext?.callState)
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>
    const wasInCall = prevCallState.current != null
    const callEnded = phoneDeviceContext?.callState == null
    if (wasInCall && callEnded && phoneDeviceContext?.voeId) {
      // Small delay to let the backend finish creating the audit trail
      timerId = setTimeout(() => refetchList(), 2000)
    }
    prevCallState.current = phoneDeviceContext?.callState
    return () => clearTimeout(timerId)
  }, [phoneDeviceContext?.callState])

  const onRowClick = (voe: VerificationOfEmployment) => {
    setSelectedVoe(voe)
    openDrawer()
  }

  // Column order follows the processor's decision flow:
  // triage (urgency) → context (who, stage, status) → action (attempts, employer, contact) → reference (period)
  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: 2,
      render: (voe: VerificationOfEmployment) => (
        <input
          type="checkbox"
          checked={selectedIds.has(voe.voeId)}
          onChange={() => toggleSelect(voe.voeId)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: 'applicant',
      label: (
        <SortableHeader
          label="Applicant"
          field="APPLICANT_NAME"
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
      ),
      width: 16,
      render: (voe: VerificationOfEmployment) => {
        const name = voe.applicant
          ? buildFullName(voe.applicant)
          : voe.driverName || '–'
        const applicantId = voe.applicant?.applicantId
        const actions = (voe as any).actions ?? []
        const hasOutreach = actions.length > 0
        const showNoOutreachFlag =
          isAdminOrOps(currentUser) &&
          !TERMINAL_VOE_STATUSES.has(voe.status as VoEStatus) &&
          !hasOutreach
        return (
          <div className="flex w-full items-center gap-1">
            <Tooltip label={name} position="top" openDelay={300}>
              <span className="block min-w-0 flex-1 truncate text-xs font-medium text-doubleNickel-gray-900">
                {name}
              </span>
            </Tooltip>
            {showNoOutreachFlag && (
              <Tooltip label="No outreach is scheduled for this VoE" withArrow>
                <span className="flex-shrink-0 rounded bg-doubleNickel-warning-50 px-1.5 py-0.5 text-[10px] font-medium text-doubleNickel-warning-600">
                  Off
                </span>
              </Tooltip>
            )}
            <ResponseTag voe={voe} />
            {applicantId && (
              <Tooltip label="Open applicant details" position="top">
                <button
                  className="ml-auto flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    openApplicantDrawer(applicantId)
                  }}
                >
                  <ArrowUpRight className="h-3 w-3 text-doubleNickel-gray-400 hover:text-doubleNickel-brand-500" />
                </button>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      key: 'status',
      label: (
        <Tooltip label="Verification status" position="top">
          <span>Status</span>
        </Tooltip>
      ),
      width: 10,
      render: (voe: VerificationOfEmployment) => (
        <StyledBadge
          size="xs"
          textTransform="capitalize"
          color={colorByStatus[voe.status]}
        >
          {formatSnakeValue(voe.status)}
        </StyledBadge>
      ),
    },
    {
      key: 'nextAction',
      label: (
        <div className="flex items-center gap-1">
          <SortableHeader
            label="Next action"
            field="NEXT_ACTION"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
          <ColumnFilterDropdown
            options={NEXT_ACTION_OWNER_OPTIONS}
            selected={nextActionFilters}
            onChange={onNextActionFiltersChange}
          />
        </div>
      ),
      width: 12,
      render: (voe: VerificationOfEmployment) => <NextActionTag voe={voe} />,
    },
    {
      key: 'consent',
      label: (
        <div className="flex items-center gap-1">
          <Tooltip
            label="Whether the applicant consented to contact this employer"
            position="top"
          >
            <span>Consent</span>
          </Tooltip>
          <ColumnFilterDropdown
            options={[
              {
                label: 'Has Consent',
                value: 'HAS_CONSENT',
                icon: (
                  <ShieldCheck className="h-3 w-3 text-doubleNickel-success-500" />
                ),
              },
              {
                label: 'No Consent',
                value: 'NO_CONSENT',
                icon: (
                  <ShieldAlert className="h-3 w-3 text-doubleNickel-warning-400" />
                ),
              },
            ]}
            selected={consentFilters}
            onChange={onConsentFiltersChange}
          />
        </div>
      ),
      width: 6,
      render: (voe: VerificationOfEmployment) => {
        const hasConsent = voe.shouldContactEmployer === true
        return hasConsent ? (
          <Tooltip
            label="Applicant consented to contact this employer"
            position="top"
          >
            <span className="flex items-center gap-1.5 text-xs text-doubleNickel-success-600">
              <ShieldCheck className="h-3.5 w-3.5" />
              Yes
            </span>
          </Tooltip>
        ) : (
          <Tooltip label="No consent to contact this employer" position="top">
            <span className="flex items-center gap-1.5 text-xs text-doubleNickel-warning-500">
              <ShieldAlert className="h-3.5 w-3.5" />
              No
            </span>
          </Tooltip>
        )
      },
    },
    {
      key: 'attempts',
      label: (
        <Tooltip
          label="Number of contact attempts (calls, emails, faxes)"
          position="top"
        >
          <span>#</span>
        </Tooltip>
      ),
      width: 3,
      render: (voe: VerificationOfEmployment) => {
        const attempts = voe.contactAttemptCount ?? 0
        return (
          <span className="text-xs text-doubleNickel-gray-900">{attempts}</span>
        )
      },
    },
    {
      key: 'followUp',
      label: (
        <div className="flex items-center gap-1">
          <SortableHeader
            label="Last Contact"
            field="LAST_CONTACT_DATE"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
          <ColumnFilterDropdown
            options={[
              {
                label: 'Never contacted',
                value: 'NEVER_CONTACTED',
                icon: (
                  <AlertTriangle className="h-3 w-3 text-doubleNickel-error-500" />
                ),
              },
              {
                label: '7+ days stale',
                value: 'STALE_7_DAYS',
                icon: (
                  <Clock className="h-3 w-3 text-doubleNickel-warning-400" />
                ),
              },
            ]}
            selected={lastContactFilters}
            onChange={onLastContactFiltersChange}
          />
        </div>
      ),
      width: 9,
      render: (voe: VerificationOfEmployment) => {
        const date = resolveLastContactDate(voe)
        if (!date) {
          return (
            <Tooltip label="No contact attempts recorded" position="top">
              <span className="flex items-center gap-1 whitespace-nowrap text-xs text-doubleNickel-gray-500">
                <AlertTriangle className="h-3 w-3 text-doubleNickel-error-500" />
                Never
              </span>
            </Tooltip>
          )
        }
        const daysSince = dayjs().diff(dayjs(date), 'day')
        const timeAgo = dayjs(date).fromNow()
        const isStale = daysSince >= 7
        return (
          <Tooltip
            label={dayjs(date).format('MM/DD/YYYY [at] h:mm A')}
            position="top"
          >
            <span className="flex items-center gap-1 whitespace-nowrap text-xs text-doubleNickel-gray-900">
              {isStale && (
                <Clock className="h-3 w-3 text-doubleNickel-warning-400" />
              )}
              {timeAgo}
            </span>
          </Tooltip>
        )
      },
    },
    {
      key: 'companyName',
      label: (
        <Tooltip label="Previous employer to verify" position="top">
          <span>Previous Employer</span>
        </Tooltip>
      ),
      width: 16,
      render: (voe: VerificationOfEmployment) => {
        const tooltipText = voe.dotNumber
          ? `${voe.companyName} · DOT: ${voe.dotNumber}`
          : voe.companyName
        const classificationReason = getEmployerClassificationLabel(
          voe.employerClassification
        )
        const platformLabel = knownPlatformLabel(
          voe.carrierRouting?.preferredPlatform
        )
        return (
          <div className="flex w-full items-center gap-1.5">
            <Tooltip label={tooltipText} position="top" openDelay={300}>
              <span className="block min-w-0 flex-1 truncate text-xs text-doubleNickel-gray-900">
                {voe.companyName || '–'}
              </span>
            </Tooltip>
            {classificationReason && (
              <span className="flex-shrink-0">
                <StyledBadge
                  color="gray"
                  compact="xs"
                  tooltipText={`Not routed for outreach — ${classificationReason}`}
                >
                  Skipped Company
                </StyledBadge>
              </span>
            )}
            {/* Right-aligned, together: the directory contacts badge (same rule
                as the employment table: a count above 0 only on a row with no
                contact yet, "Opted out" on a suppressed one), the platform, and the
                FMCSA link. The contacts badge lives with the employer because
                it is about the employer's DOT, and the contact column is too
                narrow to hold a pill beside its icons. */}
            <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
              <ContactCountBadge
                item={toContactBadgeInput(voe)}
                subject="verification"
                hideWhenNone
              />
              {platformLabel && (
                <StyledBadge
                  color="blue"
                  compact="xs"
                  tooltipText={`This employer answers verifications through ${platformLabel}`}
                >
                  {platformLabel}
                </StyledBadge>
              )}
              {voe.dotNumber && (
                <Tooltip
                  label={`FMCSA lookup · DOT ${voe.dotNumber}`}
                  position="top"
                >
                  <a
                    href={buildFmcsaCarrierUrl(voe.dotNumber)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0"
                  >
                    <ExternalLink className="h-3 w-3 text-doubleNickel-gray-400 hover:text-doubleNickel-brand-500" />
                  </a>
                </Tooltip>
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: 'contact',
      label: (
        <div className="flex items-center gap-1">
          <Tooltip label="Available contact methods" position="top">
            <span>Contact</span>
          </Tooltip>
          <ColumnFilterDropdown
            options={[
              {
                label: 'Has Phone',
                value: 'HAS_PHONE',
                icon: <Phone className="h-3 w-3 text-doubleNickel-gray-500" />,
              },
              {
                label: 'Has Email',
                value: 'HAS_EMAIL',
                icon: <Mail className="h-3 w-3 text-doubleNickel-gray-500" />,
              },
              {
                label: 'VoE Voice Agent',
                value: 'VOE_VOICE_AGENT',
                icon: (
                  <IconVoiceMail className="h-3 w-3 fill-none stroke-doubleNickel-brand-500" />
                ),
              },
              {
                label: 'No Contact Info',
                value: 'NO_CONTACT_INFO',
                icon: <Phone className="h-3 w-3 text-doubleNickel-error-400" />,
              },
            ]}
            selected={contactInfoFilters}
            onChange={onContactInfoFiltersChange}
          />
        </div>
      ),
      width: 6,
      render: (voe: VerificationOfEmployment) => {
        const hasPhone = Boolean(voe.phoneNumber)
        const hasEmail = Boolean(voe.email)
        const phoneSuppressed = Boolean(voe.contactSuppression?.phoneNumber)
        const emailSuppressed = Boolean(voe.contactSuppression?.email)
        return (
          <div className="flex items-center gap-2">
            {hasPhone && phoneSuppressed ? (
              <Tooltip
                label={`${formatInternationalPhoneNumber(
                  voe.phoneNumber
                )} — this contact asked not to be called`}
                position="top"
              >
                <PhoneOff
                  aria-label="Phone suppressed"
                  className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-warning-500"
                />
              </Tooltip>
            ) : hasPhone ? (
              <Tooltip
                label={formatInternationalPhoneNumber(voe.phoneNumber)}
                position="top"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCallClick(voe)
                  }}
                >
                  <Phone className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-gray-500 hover:text-doubleNickel-brand-500" />
                </button>
              </Tooltip>
            ) : (
              <Tooltip label="No phone number" position="top">
                <Phone className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-error-400" />
              </Tooltip>
            )}
            {hasEmail && emailSuppressed ? (
              <Tooltip
                label={`${voe.email} — this contact asked not to be emailed`}
                position="top"
              >
                <MailX
                  aria-label="Email suppressed"
                  className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-warning-500"
                />
              </Tooltip>
            ) : hasEmail ? (
              <Tooltip label={voe.email} position="top">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEmailClick(voe)
                  }}
                >
                  <Mail className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-gray-500 hover:text-doubleNickel-brand-500" />
                </button>
              </Tooltip>
            ) : (
              <Tooltip
                label="No email address — click to enter one"
                position="top"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEmailClick(voe)
                  }}
                >
                  <Mail className="h-3.5 w-3.5 flex-shrink-0 text-doubleNickel-error-400 hover:text-doubleNickel-brand-500" />
                </button>
              </Tooltip>
            )}
            <VoEVoiceAgentButton
              voeId={voe.voeId}
              phoneNumber={voe.phoneNumber}
              email={voe.email}
              canceledOutOfWindow={voe.canceledOutOfWindow}
              callingHours={voe.callingHours}
              className="-mx-1"
              guardContact={(startCall) => guardContact(voe, startCall)}
            />
          </div>
        )
      },
    },
    {
      key: 'empPeriod',
      label: (
        <div className="flex items-center gap-1">
          <Tooltip
            label={`Start and end dates of employment (strikethrough = over ${formatVerificationPeriod(
              verificationPeriodMonths
            )} ago)`}
            position="top"
          >
            <span>Employment Period</span>
          </Tooltip>
          <ColumnFilterDropdown
            options={[
              {
                label: `Within period (last ${formatVerificationPeriod(
                  verificationPeriodMonths
                )})`,
                value: 'WITHIN_PERIOD',
                icon: (
                  <CalendarCheck className="h-3 w-3 text-doubleNickel-success-500" />
                ),
              },
              {
                label: `Expired (over ${formatVerificationPeriod(
                  verificationPeriodMonths
                )})`,
                value: 'EXPIRED',
                icon: (
                  <AlertTriangle className="h-3 w-3 text-doubleNickel-warning-400" />
                ),
              },
            ]}
            selected={empPeriodFilters}
            onChange={onEmpPeriodFiltersChange}
          />
        </div>
      ),
      width: 11,
      render: (voe: VerificationOfEmployment) => {
        const startDate = voe.requestContent?.startDate
        const endDate = voe.requestContent?.endDate

        if (!startDate) {
          return <span className="text-xs text-doubleNickel-gray-400">—</span>
        }

        const startFormatted = dayjs(startDate).format('MM/YYYY')
        const endFormatted = endDate
          ? dayjs(endDate).format('MM/YYYY')
          : 'Present'
        const expired = isExpiredEmployment(endDate, verificationPeriodMonths)

        if (expired) {
          return (
            <Tooltip
              label={`Ended ${dayjs(endDate).format(
                'MMM YYYY'
              )} — over ${formatVerificationPeriod(
                verificationPeriodMonths
              )} ago`}
              position="top"
            >
              <span
                className="whitespace-nowrap text-xs text-doubleNickel-gray-400 line-through"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {startFormatted} – {endFormatted}
              </span>
            </Tooltip>
          )
        }

        return (
          <Tooltip
            label={`${dayjs(startDate).format('MMM YYYY')} – ${
              endDate ? dayjs(endDate).format('MMM YYYY') : 'Present'
            }`}
            position="top"
          >
            <span
              className="whitespace-nowrap text-xs text-doubleNickel-gray-900"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {startFormatted} – {endFormatted}
            </span>
          </Tooltip>
        )
      },
    },
    // Conditional date column: Hired Date for hired tab, Applied On for others
    ...(hiringStageTab === 'hired'
      ? [
          {
            key: 'hiredDate',
            label: (
              <SortableHeader
                label="Hired Date"
                field="APPLICANT_HIRED_DATE"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            ),
            width: 9,
            render: (voe: VerificationOfEmployment) => {
              const hiredDate = voe.applicant?.hiredDate
              if (!hiredDate) {
                return (
                  <span className="text-xs text-doubleNickel-gray-400">—</span>
                )
              }
              return (
                <Tooltip
                  label={dayjs(hiredDate).format('MMM D, YYYY')}
                  position="top"
                >
                  <span className="whitespace-nowrap text-xs text-doubleNickel-gray-900">
                    {dayjs(hiredDate).format('MM/DD/YYYY')}
                  </span>
                </Tooltip>
              )
            },
          },
        ]
      : [
          {
            key: 'appliedOn',
            label: (
              <SortableHeader
                label="Applied On"
                field="APPLICANT_APPLICATION_DATE"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            ),
            width: 9,
            render: (voe: VerificationOfEmployment) => {
              const applicationDate = voe.applicant?.applicationDate
              if (!applicationDate) {
                return (
                  <span className="text-xs text-doubleNickel-gray-400">—</span>
                )
              }
              return (
                <Tooltip
                  label={dayjs(applicationDate).format('MMM D, YYYY')}
                  position="top"
                >
                  <span className="whitespace-nowrap text-xs text-doubleNickel-gray-900">
                    {dayjs(applicationDate).format('MM/DD/YYYY')}
                  </span>
                </Tooltip>
              )
            },
          },
        ]),
  ]

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-2 pb-2">
        <div className="flex items-center gap-0.5 rounded-lg bg-doubleNickel-gray-100 p-0.5">
          {HIRING_STAGE_TABS.map((tab) => {
            const count = tabCounts?.[tab.value]
            return (
              <CustomTab
                key={tab.value}
                selectedTab={hiringStageTab}
                value={tab.value}
                label={
                  <span>
                    {tab.label}
                    {count != null && (
                      <span className="ml-1 text-doubleNickel-gray-400">
                        {count}
                      </span>
                    )}
                  </span>
                }
                setSelectedTab={(val) => {
                  onHiringStageTabChange(val)
                }}
                variant="segmented"
              />
            )
          })}
        </div>
        {selectedIds.size > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            loading={isBulkLoading}
            actions={[
              {
                label: 'Send Email',
                icon: <Mail className="h-3 w-3" />,
                onClick: bulkSendEmail,
              },
              {
                label: 'Set Status',
                icon: <CircleDot className="h-3 w-3" />,
                menu: BULK_STATUS_OPTIONS.map((status) => ({
                  label: formatSnakeValue(status),
                  onClick: () => onBulkStatusSelect(status),
                })),
              },
            ]}
          />
        )}
        {toolbarControls}
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={voeList}
          loading={loading}
          loadingRows={10}
          totalCount={totalCount}
          onLoadPage={onLoadPage}
          onRowClick={(_index, rowData) => onRowClick(rowData)}
          emptyStateMessage="No outgoing employment verifications found"
        />
      </div>
      {selectedVoe && (
        <VoEDetails
          voe={selectedVoe}
          opened={drawerOpened}
          onClose={closeDrawer}
          applicantId={selectedVoe.applicant?.applicantId ?? ''}
          onContact={(voe) => {
            setContactVoe(voe)
            openContactModal()
          }}
          onViewDetail={(voe) => {
            if (voe.applicant?.applicantId) {
              navigate(
                routes.applicantDetails({
                  id: voe.applicant.applicantId,
                  tab: 'voe',
                })
              )
            } else {
              navigate(routes.voeRequestDetails({ id: voe.voeId }))
            }
          }}
          onRender={['VerificationOfEmployments', 'VoETabCounts']}
        />
      )}
      {contactModalOpened && contactVoe && (
        <ContactModal
          voe={contactVoe}
          isOpen={contactModalOpened}
          onRender={['VerificationOfEmployments', 'VoETabCounts']}
          onClose={closeContactModal}
          onContactSuccess={undefined}
        />
      )}
      <CompletionMethodModal
        opened={bulkCompletionModalOpened}
        onClose={closeBulkCompletionModal}
        onSubmit={onBulkCompletionSubmit}
        loading={bulkUpdatingStatus || bulkCompleting}
        title={
          selectedIds.size === 1
            ? 'Employment Verification'
            : `${selectedIds.size} Employment Verifications`
        }
        {...(selectedIds.size === 1 && {
          applicantName: voeCompletionContext(singleSelectedVoe).applicantName,
          companyName: voeCompletionContext(singleSelectedVoe).companyName,
        })}
      />
      {emailModalOpened && emailVoe && (
        <EmailSenderModal
          from={currentUser?.email}
          to={getRecipientEmail(emailVoe)}
          title="Send instructions via Email"
          subject={`Employment verification request from ${currentUser?.companyName}`}
          body={buildVoEInstructionsEmailBody({
            companyName: currentUser?.companyName,
            applicantName: emailVoe.applicant
              ? buildFullName(emailVoe.applicant)
              : emailVoe.driverName ?? 'a driver',
            pastEmployerName: emailVoe.companyName ?? '',
            recruiterEmail: currentUser?.email,
            companyPhone: companyPhoneNumber ?? '',
          })}
          isOpen={emailModalOpened}
          onClose={closeEmailModal}
          onSend={({ email }) =>
            guardScheduledOutreach(
              {
                voeId: emailVoe.voeId,
                kind: 'EMAIL',
                savedEmail: emailVoe.email,
                recipient: email,
              },
              {
                manual: async () => {
                  try {
                    await sendInstructionsEmail({
                      variables: { emailTo: email, voeId: emailVoe.voeId },
                    })
                    toast('Email sent', 'success')
                    closeEmailModal()
                    refetchList()
                  } catch (error) {
                    toast(error?.message || 'Failed to send email', 'error')
                  }
                },
                onSentNow: () => {
                  closeEmailModal()
                  refetchList()
                },
              }
            )
          }
          loading={sendingEmail}
        />
      )}
      {outreachEmail.panel}
      {scheduledOutreachPrompt}
      {noContactPrompt}
    </div>
  )
}

export default OutboundVoETable
